import { TABLE_TOP_CORNERS } from "../../v2/components/tableHeaderCorners.js";
// src/features/admin/AuditLogPanel.jsx
//
// INGEST-POST-APPLY-FIXES: Admin → Governance → Audit log panel.
// Renders a paginated, read-only table of audit_log rows with four filter
// controls: date range, record/entity ID, source, and action type.
// Fetches from GET /api/audit (admin-only endpoint in operations.js).

import React, { useState, useEffect, useCallback, useRef } from "react";
import { InternalLink } from "../../components/InternalLink.jsx";
import { T } from "../../app/tokens.js";
import { OverflowTable } from "../../v2/components/ListOverflow.jsx";
import { log } from "../../utils/logger.js";
import { DateInput } from "../../components/DateInput.jsx";
import { useHoverIntent } from "../../components/useHoverIntent.js";
import { buildLocationUrl, buildMarketUrl, buildProjectUrl, buildLeaseUrl } from "../../services/buildUrl.js";
import { getDevActAsStatus } from "../../data/devActAsClient.js";

async function jsonRequest(url) {
  const res = await fetch(url, { credentials: "include" });
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return res.json();
}

const DEFAULT_PAGE_SIZE = 50;
const PAGE_SIZE_OPTIONS = [25, 50, 100];
const PAGE_SIZE_STORAGE_KEY = "auditLogPageSize";

function readSavedPageSize() {
  try {
    const savedPageSize = Number(window.localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    return PAGE_SIZE_OPTIONS.includes(savedPageSize) ? savedPageSize : DEFAULT_PAGE_SIZE;
  } catch {
    return DEFAULT_PAGE_SIZE;
  }
}

function AuditEventList({ events = [] }) {
  return (
    <ol style={{ margin: "6px 0 0", paddingLeft: 20 }}>
      {events.map((event) => (
        <li key={event.id} style={{ marginBottom: 6 }}>
          <div>
            <strong>{event.action}</strong>
            {event.entityId ? ` · ${event.entityId}` : ""}
            {event.occurredAt ? ` · ${fmtTs(event.occurredAt)}` : ""}
          </div>
          <pre style={{
            margin: "3px 0 0",
            padding: 6,
            background: T.white,
            borderRadius: 3,
            whiteSpace: "pre-wrap",
            overflowWrap: "anywhere",
            fontFamily: "ui-monospace, monospace",
          }}>
            {JSON.stringify({ before: event.before, after: event.after }, null, 2)}
          </pre>
        </li>
      ))}
    </ol>
  );
}

function AuditGroupDetails({ row }) {
  if (row.groupKind === "import_batch") {
    return (
      <details
        data-testid={`audit-group-details-${row.id}`}
        style={{ marginTop: 6, fontSize: T.fsXxs, color: T.mid }}
      >
        <summary style={{ cursor: "pointer" }}>
          View {row.groupJobCount} import jobs
        </summary>
        <ol style={{ margin: "6px 0 0", paddingLeft: 20 }}>
          {(row.groupDetails || []).map((job) => (
            <li key={job.jobId} style={{ marginBottom: 6 }}>
              <strong>
                Import job {job.jobId} · {displayLabel(job.status, "Unknown status")}
              </strong>
              {job.rowCount != null && Number.isFinite(Number(job.rowCount)) && ` · ${Number(job.rowCount).toLocaleString()} rows`}
              {job.appliedCount != null && Number.isFinite(Number(job.appliedCount)) && ` · ${Number(job.appliedCount).toLocaleString()} applied`}
              <details style={{ marginTop: 4 }}>
                <summary style={{ cursor: "pointer" }}>
                  View {job.eventCount} original audit events
                </summary>
                <AuditEventList events={job.auditDetails} />
              </details>
            </li>
          ))}
        </ol>
      </details>
    );
  }

  return (
    <details
      data-testid={`audit-group-details-${row.id}`}
      style={{ marginTop: 6, fontSize: T.fsXxs, color: T.mid }}
    >
      <summary style={{ cursor: "pointer" }}>
        View {row.groupEventCount} original audit events
      </summary>
      <AuditEventList events={row.groupDetails} />
    </details>
  );
}

export const AUDIT_SOURCE_OPTIONS = [
  { value: "",           label: "All sources" },
  { value: "client",     label: "Client" },
  { value: "trigger",    label: "Trigger" },
  { value: "migration",  label: "Migration" },
  { value: "reconciler", label: "Reconciler" },
  { value: "trigger:oidc", label: "Trigger · OIDC" },
  { value: "trigger:dev_auth", label: "Trigger · Development auth" },
  { value: "trigger:test_fixture", label: "Trigger · Test fixture" },
];

// Currency fields that should be formatted with a $ prefix.
const CURRENCY_FIELDS = new Set([
  "total_cost", "rent", "base_rent", "annual_rent", "annual_cost",
  "opex", "capex", "abatement", "ti_allowance", "allowance",
]);

function fmtTs(ts) {
  if (!ts) return "—";
  try {
    return new Date(ts).toLocaleString("en-US", {
      year: "numeric", month: "short", day: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return String(ts);
  }
}

function fmtValue(fieldKey, val) {
  if (val == null) return "—";
  if (fieldKey && CURRENCY_FIELDS.has(fieldKey)) {
    const n = Number(val);
    if (!Number.isNaN(n)) return `$${n.toLocaleString()}`;
  }
  const s = typeof val === "string" ? val : JSON.stringify(val);
  return s;
}

// Keys that are never meaningful for a human-readable change summary.
const SKIP_KEYS = new Set([
  "reason", "session", "actor", "count",
  "updated_at", "updatedAt", "id",
]);

// Loosely compare two values: treat null/undefined/empty-string as equivalent.
function isEmpty(v) {
  return v === null || v === undefined || v === "";
}
function valuesEqual(a, b) {
  if (isEmpty(a) && isEmpty(b)) return true;
  // Coerce to string for a stable comparison that handles number/string drift
  // in JSON-round-tripped JSONB columns.
  return String(a) === String(b);
}

function getChangedFields({ before, after }) {
  const hasAfter = after && typeof after === "object" && !Array.isArray(after);
  if (!hasAfter) return [];
  const hasBefore = before && typeof before === "object" && !Array.isArray(before);
  const allKeys = Array.from(new Set([
    ...Object.keys(after),
    ...(hasBefore ? Object.keys(before) : []),
  ])).filter((key) => !SKIP_KEYS.has(key));

  return allKeys.filter((key) => !valuesEqual(
    hasBefore ? before[key] : undefined,
    after[key],
  ));
}

// Returns a short human-readable sentence describing what changed.
// Exported for unit-testing.
export function describeChange({ action, before, after }) {
  if (!action) return "—";

  const hasAfter = after && typeof after === "object" && !Array.isArray(after);
  const hasBefore = before && typeof before === "object" && !Array.isArray(before);

  // Detect create / insert
  const isCreate = /\.(create|insert|add|new)$/i.test(action) || (!hasBefore && hasAfter);
  if (isCreate && !hasBefore) {
    return "Record created";
  }

  // Detect delete / remove
  const isDelete = /\.(delete|remove|destroy)$/i.test(action) || (hasBefore && !hasAfter);
  if (isDelete && !hasAfter) {
    return "Record deleted";
  }

  // Find fields that actually changed by diffing before vs after.
  if (hasAfter) {
    const allKeys = Array.from(new Set([
      ...Object.keys(after),
      ...(hasBefore ? Object.keys(before) : []),
    ])).filter((k) => !SKIP_KEYS.has(k));
    const changedKeys = getChangedFields({ before, after });
    const fieldKey = changedKeys[0];
    if (fieldKey) {
      const prev = hasBefore ? before[fieldKey] : undefined;
      const next = after[fieldKey];
      const label = fieldKey.replace(/_/g, " ");

      if (prev !== undefined && !isEmpty(prev)) {
        const fPrev = fmtValue(fieldKey, prev);
        const fNext = isEmpty(next) ? "(cleared)" : fmtValue(fieldKey, next);
        // Truncate for readability in the "What changed" column
        const short = (s) => (s && s.length > 40 ? s.slice(0, 40) + "…" : s);
        return `${label}: ${short(fPrev)} → ${short(fNext)}`;
      }
      // Field set for first time
      const fNext = isEmpty(next) ? "(empty)" : fmtValue(fieldKey, next);
      const short = (s) => (s && s.length > 40 ? s.slice(0, 40) + "…" : s);
      return `${label} set to ${short(fNext)}`;
    }

    // All fields identical — action fired but nothing diffed (e.g. no-op patch)
    if (allKeys.length > 0) {
      const cleaned = action.replace(/[._]/g, " ").replace(/^\w/, (c) => c.toUpperCase());
      return cleaned;
    }
  }

  // Fall back to a cleaned-up action string
  const cleaned = action.replace(/[._]/g, " ").replace(/^\w/, (c) => c.toUpperCase());
  return cleaned;
}

function displayLabel(value, fallback = "Unknown") {
  if (value == null || String(value).trim() === "") return fallback;
  const text = String(value).replace(/[_\.]/g, " ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

function userIdentity(image, entityId) {
  const name = image?.display_name || image?.displayName || image?.name;
  const email = image?.email;
  const identity = name || email || (entityId ? `user ${entityId}` : "user");
  return email && name ? `${name} (${email})` : identity;
}

// Plain-English event description shown separately from the structured diff.
export function describeAuditDescription(row = {}) {
  const { action, before, after, entityType, entityId } = row;
  if (row.isGroupSummary) {
    if (row.groupKind === "wpr_week") {
      return `WPR snapshots for week of ${after?.weekDate || entityId} · ${Number(row.groupEventCount) || 0} events`;
    }
    const status = displayLabel(after?.status, "Unknown status");
    const rowCount = Number(after?.rowCount);
    const appliedCount = Number(after?.appliedCount);
    const jobId = entityId || row.sourceSessionId;
    const summary = [
      row.groupKind === "import_batch"
        ? `Import batch ${jobId ?? ""}`.trim()
        : jobId == null ? "Import job" : `Import job ${jobId}`,
      status,
    ];
    if (row.groupKind === "import_batch" && row.groupJobCount != null && Number.isFinite(Number(row.groupJobCount))) {
      summary.push(`${Number(row.groupJobCount).toLocaleString()} jobs`);
    }
    if (after?.rowCount != null && Number.isFinite(rowCount)) summary.push(`${rowCount.toLocaleString()} rows`);
    if (after?.appliedCount != null && Number.isFinite(appliedCount)) summary.push(`${appliedCount.toLocaleString()} applied`);
    return summary.join(" · ");
  }
  if (!action) return "—";

  if (action === "user.create") {
    const role = displayLabel(after?.role, "unknown role");
    return `Created ${userIdentity(after, entityId)} with role ${role}`;
  }
  if (action === "user.delete") {
    return `Deleted ${userIdentity(before, entityId)}`;
  }
  if (action === "user.update" && !valuesEqual(before?.role, after?.role)) {
    const oldRole = displayLabel(before?.role, "no role");
    const newRole = displayLabel(after?.role, "no role");
    return `Changed role for ${userIdentity(after, entityId)} from ${oldRole} to ${newRole}`;
  }
  if (action === "sensitivity.deny") {
    const resource = after?.areaKey || after?.resource || entityId;
    return `Denied access to ${displayLabel(resource, "restricted resource")} for the ${displayLabel(after?.role, "Unknown")} role`;
  }

  const change = describeChange(row);
  if (/(?:^|[._])(update|patch)$/i.test(action)) {
    const recordType = displayLabel(entityType, "record").toLowerCase();
    const record = entityId ? ` ${entityId}` : "";
    return `Updated ${recordType}${record}: ${change}`;
  }
  return change;
}

// Returns a display string for who performed the action.
// Exported for unit-testing.
export function describeWho({ userName, userEmail }) {
  if (userName) return userName;
  if (userEmail) return userEmail;
  return "System";
}

const ENTITY_AREAS = {
  location: "Offices",
  lease: "Leases",
  project: "Projects",
  market: "Markets",
  user: "Users",
  allowance: "Leases",
  draw: "Leases",
  critical_date: "Leases",
  watch_item: "Leases",
  lease_document: "Leases",
  candidate: "Projects",
  commentary: "Projects",
  project_budget: "Projects",
  project_document: "Projects",
  project_source: "Projects",
  milestone: "Projects",
  decision: "Projects",
  strategic_directive: "Projects",
  fx_rate: "FX Rates",
  import_session: "Imports",
};

const ENTITY_NAME_KEYS = {
  user: ["display_name", "displayName", "name", "email"],
  location: ["location_alias", "locationAlias", "name", "location_name", "locationName"],
  market: ["market_name", "marketName", "name"],
  project: ["project_name", "projectName", "name", "title"],
  candidate: ["project_name", "projectName", "name", "title"],
  project_document: ["title", "name", "source", "pageRef"],
  project_source: ["displayName", "display_name", "label", "name", "sourceType", "source_type"],
  project_budget: ["label", "name", "category", "title"],
  milestone: ["title", "name", "label"],
  decision: ["title", "name", "label"],
  strategic_directive: ["title", "name", "label"],
  lease: ["lease_name", "leaseName", "name", "location_alias", "locationAlias"],
};

const DENIAL_AREAS = {
  lease: "Leases", lease_financials: "Leases", leases: "Leases",
  location: "Offices", locations: "Offices", office: "Offices", offices: "Offices",
  project: "Projects", projects: "Projects", market: "Markets", markets: "Markets",
  report: "Reports", reports: "Reports", admin: "Admin",
};

function snapshotFor(row = {}) {
  return row.after && typeof row.after === "object" ? row.after
    : row.before && typeof row.before === "object" ? row.before : {};
}

function firstSnapshotValue(row, keys) {
  for (const snapshot of [row.after, row.before]) {
    if (!snapshot || typeof snapshot !== "object") continue;
    for (const key of keys) {
      const value = snapshot[key];
      if (value != null && String(value).trim()) return String(value);
    }
  }
  return null;
}

function denialArea(row) {
  const snapshot = snapshotFor(row);
  const key = String(snapshot.areaKey || snapshot.resource || "").toLowerCase();
  const areaMatch = Object.entries(DENIAL_AREAS).find(([needle]) => key.includes(needle));
  if (areaMatch) return areaMatch[1];
  const endpoint = String(row.entityId || snapshot.endpoint || "").toLowerCase();
  const endpointMatch = Object.entries({
    "/leases": "Leases", "/locations": "Offices", "/offices": "Offices",
    "/projects": "Projects", "/markets": "Markets", "/reports": "Reports", "/admin": "Admin",
  }).find(([needle]) => endpoint.includes(needle));
  return endpointMatch?.[1] || "Access";
}

export function describeAuditLocation(row = {}) {
  const entityType = String(row.entityType || "").toLowerCase();
  const isDenial = row.action === "sensitivity.deny";
  const area = isDenial ? denialArea(row) : (ENTITY_AREAS[entityType] || displayLabel(entityType, "Audit"));
  const nameKeys = ENTITY_NAME_KEYS[entityType] || ["name", "title", "label", "display_name", "displayName"];

  let name = isDenial
    ? firstSnapshotValue(row, ["recordName", "record_name", "resourceName", "resource_name"])
    : firstSnapshotValue(row, nameKeys);
  if (!name && isDenial) {
    const endpointSegments = String(row.entityId || "").split("/").filter((part) => part && part !== "api" && !["rows", "list", "detail"].includes(part));
    const endpointLeaf = endpointSegments.at(-1);
    name = endpointLeaf === "portfolio" ? "Portfolio"
      : endpointLeaf ? displayLabel(endpointLeaf.replace(/[-_]/g, " "), "Restricted area")
        : "Restricted area";
  }
  if (!name && row.entityId && !String(row.entityId).startsWith("/")) name = String(row.entityId);
  if (!name) name = "Unresolved record";

  const id = row.entityId ? encodeURIComponent(String(row.entityId)) : "";
  const parentProjectId = firstSnapshotValue(row, ["projectId", "project_id"]);
  const parentLeaseId = firstSnapshotValue(row, ["leaseId", "lease_id"]);
  const href = isDenial ? null
    : ({
      location: id ? buildLocationUrl(id) : null,
      lease: id ? buildLeaseUrl(id) : null,
      project: id ? buildProjectUrl(String(row.entityId)) : null,
      market: id ? buildMarketUrl(id) : null,
      project_budget: parentProjectId ? buildProjectUrl(parentProjectId) : null,
      project_document: parentProjectId ? buildProjectUrl(parentProjectId) : null,
      project_source: parentProjectId ? buildProjectUrl(parentProjectId) : null,
      milestone: parentProjectId ? buildProjectUrl(parentProjectId) : null,
      decision: parentProjectId ? buildProjectUrl(parentProjectId) : null,
      strategic_directive: parentProjectId ? buildProjectUrl(parentProjectId) : null,
      allowance: parentLeaseId ? buildLeaseUrl(parentLeaseId) : null,
      draw: parentLeaseId ? buildLeaseUrl(parentLeaseId) : null,
      critical_date: parentLeaseId ? buildLeaseUrl(parentLeaseId, "dates") : null,
      watch_item: parentLeaseId ? buildLeaseUrl(parentLeaseId) : null,
      lease_document: parentLeaseId ? buildLeaseUrl(parentLeaseId, "documents") : null,
    }[entityType] || null);
  return { area, name, href };
}

export function getAuditActionCategory(action = "") {
  if (/deny|denied|forbidden/i.test(action)) return "Denied";
  if (/\.(create|insert|add|new)$/i.test(action)) return "Create";
  if (/\.(delete|remove|destroy)$/i.test(action)) return "Delete";
  return "Update";
}

const FIXTURE_MARKER = /(?:\btest\b|test[-_ ]?\d|fixture|example(?:[-_ ]?fixture)?)/i;
const FIXTURE_ID = /^(?:TEST-|L\.TEST-|TST-|test-|fixture[-_])/i;
const FIXTURE_EMAIL = /(?:^test[-+_.]|@example\.|fixture)/i;

export function isAuditTestFixture(row = {}) {
  if (row.isTestFixture === true || row.is_test_fixture === true) return true;
  const entityId = String(row.entityId || "");
  if (FIXTURE_ID.test(entityId) || /(?:^|[-_.])(?:test|fixture|example)(?:$|[-_.])/i.test(entityId)) return true;
  if (/test[_-]?fixture|fixture/i.test(String(row.source || ""))) return true;
  if (FIXTURE_EMAIL.test(String(row.userEmail || ""))) return true;
  if (FIXTURE_MARKER.test(String(row.userName || ""))) return true;
  const inspect = (value, key = "") => {
    if (value == null) return false;
    if (typeof value === "string") {
      if (/email/i.test(key) && FIXTURE_EMAIL.test(value)) return true;
      if (/(?:name|id|slug|key|title|label)/i.test(key) && FIXTURE_MARKER.test(value)) return true;
      return /fixture/i.test(value);
    }
    if (Array.isArray(value)) return value.some((item) => inspect(item, key));
    if (typeof value === "object") return Object.entries(value).some(([childKey, child]) => inspect(child, childKey));
    return false;
  };
  return inspect(row.before) || inspect(row.after);
}

function formattedFieldValue(fieldKey, value) {
  if (value == null) return "—";
  if (typeof value === "number") return fmtValue(fieldKey, value);
  if (typeof value === "string" && value.trim() !== "" && Number.isFinite(Number(value))) {
    const number = Number(value);
    return CURRENCY_FIELDS.has(fieldKey) ? fmtValue(fieldKey, number) : number.toLocaleString();
  }
  return typeof value === "string" ? value : JSON.stringify(value);
}

const thStyle = {
  textAlign:     "left",
  fontSize:      T.fsXxs,
  fontWeight:    T.fwBold,
  color:         "var(--v2-mid)",
  letterSpacing: ".07em",
  textTransform: "uppercase",
  padding:       "10px 14px",
  borderBottom:  "1px solid var(--v2-line)",
  whiteSpace:    "nowrap",
};

const tdStyle = {
  fontSize: T.fsXs,
  color: T.dark,
  padding: "6px 10px",
  borderBottom: `1px solid ${T.border}`,
  verticalAlign: "top",
};

// Fixed control height used across every filter bar input/select/button.
const CTRL_H = 30;

// Column widths for the two-table scroll-sync split (header + body share
// identical colgroup so columns stay aligned).
const COL_WIDTHS = [155, 110, 140, 240, 65, 110, 110, 110];
const TABLE_MIN_W = COL_WIDTHS.reduce((s, w) => s + w, 0);

export function AuditLogPanel({ explainer } = {}) {
  const [from,     setFrom]     = useState("");
  const [to,       setTo]       = useState("");
  const [entityId, setEntityId] = useState("");
  const [source,   setSource]   = useState("");
  const [action,   setAction]   = useState("");
  const [eventClass, setEventClass] = useState("human");
  const [devCapability, setDevCapability] = useState(null);
  const [hideTestDataPreference, setHideTestData] = useState(true);
  const hideTestData = devCapability === true && hideTestDataPreference;
  useEffect(() => {
    let live = true;
    getDevActAsStatus()
      .then((status) => { if (live) setDevCapability(status?.enabled === true); })
      .catch(() => { if (live) setDevCapability(false); });
    return () => { live = false; };
  }, []);
  const helpIntent = useHoverIntent({ interactive: true });
  const [pageSize, setPageSize] = useState(readSavedPageSize);

  // Scroll-sync refs for sticky header / body split.
  // Header table sits in a position:sticky shell with overflow:hidden.
  // Body table sits in an overflowX:auto wrapper; JS syncs scrollLeft on scroll.
  const auditHeaderRef = useRef(null);
  const auditBodyRef   = useRef(null);
  const handleAuditBodyScroll = useCallback(() => {
    if (auditHeaderRef.current && auditBodyRef.current) {
      auditHeaderRef.current.scrollLeft = auditBodyRef.current.scrollLeft;
    }
  }, []);

  const [rows,    setRows]    = useState([]);
  const [pagination, setPagination] = useState({
    hasNext: false,
    hasPrevious: false,
    nextCursor: null,
    previousCursor: null,
  });
  const [pageHistory, setPageHistory] = useState([]);
  const [pagePosition, setPagePosition] = useState({ cursor: null, direction: "after" });
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const fetch = useCallback(async (cursor = null, direction = "after") => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: String(pageSize) });
      if (from)     params.set("from",     from);
      if (to)       params.set("to",       to + "T23:59:59Z");
      if (entityId) params.set("entityId", entityId.trim());
      if (source)   params.set("source",   source);
      if (action)   params.set("action",   action.trim());
      params.set("eventClass", eventClass);
      params.set("hideTestData", String(hideTestData));
      if (cursor) {
        params.set("cursor", cursor);
        params.set("direction", direction);
      }

      const data = await jsonRequest(`/api/audit?${params}`);
      setRows(data.rows || []);
      setPagination(data.pagination || {
        hasNext: false,
        hasPrevious: false,
        nextCursor: null,
        previousCursor: null,
      });
      return data;
    } catch (err) {
      log.error("AuditLogPanel fetch failed", { err: String(err) });
      setError(err?.message || "Failed to load audit log");
      return null;
    } finally {
      setLoading(false);
    }
  }, [from, to, entityId, source, action, eventClass, hideTestData, pageSize]);

  useEffect(() => {
    try {
      window.localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(pageSize));
    } catch {
      // Keep the control usable when browser storage is disabled or unavailable.
    }
  }, [pageSize]);

  useEffect(() => {
    // Confirm the default-on DEV preference before the initial load.
    // Explicit requests while pending still use hideTestData=false.
    if (devCapability === null) return;
    setPageHistory([]);
    setPagePosition({ cursor: null, direction: "after" });
    fetch(null, "after");
  }, [fetch, devCapability]);

  async function handleApply(e) {
    e.preventDefault();
    const data = await fetch(null, "after");
    if (data) {
      setPageHistory([]);
      setPagePosition({ cursor: null, direction: "after" });
    }
  }

  async function handlePrev() {
    const previousPage = pageHistory.at(-1);
    if (previousPage) {
      const data = await fetch(previousPage.cursor, previousPage.direction);
      if (data) {
        setPageHistory((history) => history.slice(0, -1));
        setPagePosition(previousPage);
      }
      return;
    }

    if (pagination.previousCursor) {
      const cursor = pagination.previousCursor;
      const data = await fetch(cursor, "before");
      if (data) setPagePosition({ cursor, direction: "before" });
    }
  }

  async function handleNext() {
    if (pagination.nextCursor) {
      const nextPage = { cursor: pagination.nextCursor, direction: "after" };
      const data = await fetch(nextPage.cursor, nextPage.direction);
      if (data) {
        setPageHistory((history) => [...history, pagePosition]);
        setPagePosition(nextPage);
      }
    }
  }

  // Base style for filter-bar text inputs and selects.
  const inputStyle = {
    height: CTRL_H,
    boxSizing: "border-box",
    fontSize: T.fsXs,
    padding: "0 8px",
    border: `1px solid ${T.border}`,
    borderRadius: 4,
    outline: "none",
    fontFamily: "inherit",
    color: T.dark,
    background: T.white,
  };

  const labelStyle = {
    fontSize: T.fsXs,
    color: T.mid,
    fontWeight: T.fwSemibold,
  };

  const visibleRows = rows;
  const hideTestHelpOpen = helpIntent.open;
  const canGoPrevious = pageHistory.length > 0 || pagination.hasPrevious;

  return (
    <div>
      <h2 style={{ fontSize: T.fsMd, fontWeight: T.fwSemibold, color: T.dark, marginBottom: 16, marginTop: 0 }}>
        Audit Log
      </h2>
      {explainer}

      {/* Filter bar */}
      <form
        onSubmit={handleApply}
        style={{ display: "flex", flexWrap: "wrap", gap: 12, alignItems: "flex-end", marginBottom: 16 }}
      >
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label style={labelStyle}>From date</label>
          <DateInput
            value={from}
            onChange={(e) => setFrom(e.target.value)}
            // fontSize: T.fsXs intentionally overrides DateInput's fsSm5 default
            // so the date pickers match the sibling text input and select.
            style={{ ...inputStyle, paddingRight: 30, minWidth: 110 }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label style={labelStyle}>To date</label>
          <DateInput
            value={to}
            onChange={(e) => setTo(e.target.value)}
            // fontSize: T.fsXs intentionally overrides DateInput's fsSm5 default
            // so the date pickers match the sibling text input and select.
            style={{ ...inputStyle, paddingRight: 30, minWidth: 110 }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label style={labelStyle}>Record / lease ID</label>
          <input
            type="text"
            placeholder="e.g. L.AMER-SFO-01.1"
            value={entityId}
            onChange={(e) => setEntityId(e.target.value)}
            style={{ ...inputStyle, width: 180 }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label style={labelStyle}>Source</label>
          <select value={source} onChange={(e) => setSource(e.target.value)} style={inputStyle}>
            {AUDIT_SOURCE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label style={labelStyle}>Action contains</label>
          <input
            type="text"
            placeholder="e.g. lease.update"
            value={action}
            onChange={(e) => setAction(e.target.value)}
            style={{ ...inputStyle, width: 160 }}
          />
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <label htmlFor="audit-event-class" style={labelStyle}>Activity</label>
          <select
            id="audit-event-class"
            aria-label="Activity"
            data-testid="audit-event-class"
            value={eventClass}
            onChange={(e) => setEventClass(e.target.value)}
            style={inputStyle}
          >
            <option value="human">Human &amp; governance</option>
            <option value="system">System &amp; automated</option>
            <option value="all">All activity</option>
          </select>
        </div>
        <button
          type="submit"
          style={{
            height: CTRL_H,
            boxSizing: "border-box",
            padding: "0 14px",
            background: T.blue,
            border: "none",
            borderRadius: 4,
            color: T.white,
            fontSize: T.fsXs,
            fontWeight: T.fwSemibold,
            cursor: "pointer",
          }}
        >
          Apply
        </button>
      </form>

      {devCapability === true && <>
      <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: T.fsXs, color: T.dark, marginBottom: 12 }}>
        <input
          type="checkbox"
          checked={hideTestData}
          onChange={(event) => setHideTestData(event.target.checked)}
          data-testid="toggle-hide-test-data"
        />
        Hide test data
      </label>
      <span
        style={{ position: "relative", display: "inline-flex", verticalAlign: "middle", margin: "0 0 12px 4px" }}
        {...helpIntent.triggerProps}
      >
        <button
          type="button"
          aria-label="About Hide test data"
          aria-describedby={hideTestHelpOpen ? "audit-hide-test-data-tooltip" : undefined}
          aria-expanded={hideTestHelpOpen}
          data-testid="audit-hide-test-data-help"
          style={{
            width: 18,
            height: 18,
            padding: 0,
            border: `1px solid ${T.border}`,
            borderRadius: "50%",
            background: T.white,
            color: T.mid,
            fontSize: T.fsXxs,
            lineHeight: "16px",
            cursor: "help",
          }}
        >
          ⓘ
        </button>
        <span
          {...helpIntent.popupProps}
          id="audit-hide-test-data-tooltip"
          role="tooltip"
          data-testid="audit-hide-test-data-explanation"
          style={{
            position: "absolute",
            zIndex: 8,
            top: 23,
            left: 0,
            width: "min(310px, calc(100vw - 32px))",
            padding: 9,
            display: hideTestHelpOpen ? "grid" : "none",
            gap: 4,
            border: `1px solid ${T.border}`,
            borderRadius: 5,
            background: T.white,
            boxShadow: "0 4px 14px rgba(0,0,0,.16)",
            color: T.mid,
            fontSize: T.fsXxs,
            lineHeight: 1.4,
          }}
        >
          <span>Test fixtures are identified by persisted fixture flags and narrow test-only markers.</span>
          <span>Dev-auth activity comes from the local development login flow, not ordinary portfolio accounts.</span>
          <span>Unknown-role denials without an actor are unauthenticated access probes, not user decisions.</span>
          <span>The portfolio-roadmap source URL is an explicit seeded test marker.</span>
        </span>
      </span>
      </>}
      <label style={{ display: "inline-flex", alignItems: "center", gap: 7, fontSize: T.fsXs, color: T.dark, marginBottom: 12, marginLeft: 16 }}>
        Rows per page
        <select
          aria-label="Rows per page"
          data-testid="audit-page-size"
          value={pageSize}
          onChange={(event) => setPageSize(Number(event.target.value))}
          style={inputStyle}
        >
          {PAGE_SIZE_OPTIONS.map((size) => (
            <option key={size} value={size}>{size}</option>
          ))}
        </select>
      </label>

      {error && (
        <div style={{ color: T.red, fontSize: T.fsXs, marginBottom: 12 }}>{error}</div>
      )}

      {/* Results summary */}
      {!loading && (
        <div style={{ fontSize: T.fsXs, color: T.collectionCount, marginBottom: 8 }}>
          {visibleRows.length === 0
            ? (pagination.hasNext || pagination.hasPrevious
                ? "No visible entries in this batch · more available"
                : "No entries match the current filters.")
            : `Showing ${visibleRows.length} entr${visibleRows.length === 1 ? "y" : "ies"} on this page${pagination.hasNext ? " · more available" : ""}`}
          {hideTestData ? " · test data hidden" : ""}
        </div>
      )}

      {/* Table — two-table scroll-sync split for sticky header.
          Header table lives inside a position:sticky shell (overflow:hidden) so
          it pins to the top of the viewport while the body scrolls. The body
          wrapper uses overflowX:auto; JS syncs scrollLeft back to the header clip
          div on every scroll event. Both tables share the same colgroup widths so
          columns stay aligned. Pattern mirrors HeadcountTrendReport / SpaceFitReport. */}
      <div style={{ border: `1px solid ${T.border}`, borderRadius: 6, ...TABLE_TOP_CORNERS }}>

        {/* ── Sticky header shell ── */}
        <div style={{ position: "sticky", top: 0, zIndex: 4, background: T.white, ...TABLE_TOP_CORNERS }}>
          <div ref={auditHeaderRef} style={{ overflowX: "hidden", ...TABLE_TOP_CORNERS }}>
            <OverflowTable pinActions overflowScrollRef={auditBodyRef} style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: TABLE_MIN_W }}>
              <colgroup>
                {COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
              </colgroup>
              <thead>
                <tr>
                  <th style={thStyle}>Date</th>
                  <th style={thStyle}>User</th>
                  <th style={thStyle}>Location</th>
                  <th style={thStyle}>What changed</th>
                  <th style={thStyle}>Field</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Previous</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>New</th>
                  <th style={thStyle}>Record</th>
                </tr>
              </thead>
            </OverflowTable>
          </div>
        </div>

        {/* ── Scrollable body ── */}
        <div
          ref={auditBodyRef}
          onScroll={handleAuditBodyScroll}
          style={{ overflowX: "auto", borderBottomLeftRadius: 6, borderBottomRightRadius: 6 }}
        >
          <OverflowTable pinActions style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed", minWidth: TABLE_MIN_W }}>
            <colgroup>
              {COL_WIDTHS.map((w, i) => <col key={i} style={{ width: w }} />)}
            </colgroup>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} style={{ ...tdStyle, textAlign: "center", color: T.mid, padding: 24 }}>
                    Loading…
                  </td>
                </tr>
              ) : visibleRows.length === 0 ? (
                <tr>
                  <td colSpan={8} style={{ ...tdStyle, textAlign: "center", color: T.mid, padding: 24 }}>
                    No audit entries found for the selected filters.
                  </td>
                </tr>
              ) : visibleRows.map((r) => {
                const fieldKey = getChangedFields(r)[0];
                const prevVal = r.before && typeof r.before === "object" && fieldKey
                  ? r.before[fieldKey]
                  : null;
                const nextVal = r.after && typeof r.after === "object" && fieldKey
                  ? r.after[fieldKey]
                  : null;
                const fixture = isAuditTestFixture(r);
                const location = describeAuditLocation(r);
                const actionCategory = getAuditActionCategory(r.action);
                const actionColors = {
                  Create: { background: T.greenSoft, color: T.successText },
                  Update: { background: T.blueSoft, color: T.blue },
                  Delete: { background: T.redSoft, color: T.urgentRed },
                  Denied: { background: T.utilAmber, color: T.amberDark },
                }[actionCategory];
                const displayId = r.entityId == null ? null : String(r.entityId);
                const shortId = displayId && displayId.length > 8 ? `${displayId.slice(0, 8)}…` : displayId;
                const displayPrevious = formattedFieldValue(fieldKey, prevVal);
                const displayNext = formattedFieldValue(fieldKey, nextVal);
                const valueStyle = {
                  ...tdStyle,
                  textAlign: "right",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  fontVariantNumeric: "tabular-nums",
                };

                return (
                  <tr key={r.id} data-testid="audit-log-row" data-fixture={fixture ? "true" : "false"} style={{ background: fixture ? T.utilAmber : T.white }}>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap" }}>
                      <time dateTime={r.occurredAt || undefined}>{fmtTs(r.occurredAt)}</time>
                    </td>
                    <td style={tdStyle}>
                      <span data-testid={`audit-log-user-${r.id}`}>{describeWho(r)}</span>
                    </td>
                    <td style={tdStyle}>
                      <span style={{ display: "block", fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".04em", color: T.mid, textTransform: "uppercase" }}>
                        {location.area}
                      </span>
                      {location.href
                        ? <InternalLink href={location.href} style={{ color: T.blue, textDecoration: "none", fontWeight: T.fwSemibold }}>{location.name}</InternalLink>
                        : <span>{location.name}</span>}
                      {fixture && (
                        <span data-testid={`audit-fixture-tag-${r.id}`} style={{ marginLeft: 6, padding: "1px 4px", borderRadius: 3, background: T.utilAmber, color: T.amberDark, fontSize: T.fsXxs, fontWeight: T.fwBold, textTransform: "uppercase" }}>
                          test
                        </span>
                      )}
                    </td>
                    <td style={tdStyle}>
                      <span style={{ display: "inline-block", marginRight: 6, padding: "1px 6px", borderRadius: 4, background: actionColors.background, color: actionColors.color, fontSize: T.fsXxs, fontWeight: T.fwBold, letterSpacing: ".03em", textTransform: "uppercase", verticalAlign: "1px" }}>
                        {actionCategory}
                      </span>
                      {describeAuditDescription(r)}
                      {r.isGroupSummary && Array.isArray(r.groupDetails) && (
                        <AuditGroupDetails row={r} />
                      )}
                    </td>
                    {/* Field — secondary metadata, intentional T.mid dim */}
                    <td style={{ ...tdStyle, color: T.mid }}>
                      {fieldKey || "—"}
                    </td>
                    <td style={valueStyle} title={displayPrevious}>
                      <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>{displayPrevious}</span>
                    </td>
                    <td style={valueStyle} title={displayNext}>
                      <span style={{ display: "block", overflow: "hidden", textOverflow: "ellipsis" }}>{displayNext}</span>
                    </td>
                    <td style={{ ...tdStyle, whiteSpace: "nowrap", fontFamily: "ui-monospace, monospace", fontSize: T.fsXxs, color: T.mid }} title={displayId || ""}>
                      <span>{shortId || "—"}</span>
                      {displayId && (
                        <button
                          type="button"
                          data-testid={`button-copy-record-${r.id}`}
                          aria-label="Copy record ID"
                          title={`Copy full record ID: ${displayId}`}
                          onClick={() => {
                            if (navigator.clipboard?.writeText) navigator.clipboard.writeText(displayId).catch(() => {});
                          }}
                          style={{ marginLeft: 5, border: 0, padding: "0 2px", background: "transparent", color: T.mid, cursor: "pointer", fontSize: T.fsXs }}
                        >
                          ⧉
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </OverflowTable>
        </div>

      </div>

      {/* Pagination */}
      {(pagination.hasNext || canGoPrevious) && (
        <div style={{ display: "flex", gap: 8, justifyContent: "flex-end", marginTop: 12 }}>
          <button
            type="button"
            onClick={handlePrev}
            disabled={!canGoPrevious || loading}
            style={{
              padding: "4px 12px",
              border: `1px solid ${T.border}`,
              borderRadius: 4,
              background: T.white,
              color: !canGoPrevious || loading ? T.mid : T.dark,
              fontSize: T.fsXs,
              cursor: !canGoPrevious || loading ? "default" : "pointer",
            }}
          >
            ← Prev
          </button>
          <button
            type="button"
            onClick={handleNext}
            disabled={!pagination.hasNext || loading}
            style={{
              padding: "4px 12px",
              border: `1px solid ${T.border}`,
              borderRadius: 4,
              background: T.white,
              color: !pagination.hasNext || loading ? T.mid : T.dark,
              fontSize: T.fsXs,
              cursor: !pagination.hasNext || loading ? "default" : "pointer",
            }}
          >
            Next →
          </button>
        </div>
      )}
    </div>
  );
}
